import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { CommonRootState } from '@common/types/common-root-state-type.d';
import { WebRootState } from '@website/store/web-store';
import {
  selectKbAdoptionData,
  selectKbAdoptionState
} from '../BotUsageDashboardSelectors';

interface KBAdoptionData {
  totalKBs: number | null;
  totalDocuments: number | null;
  agentsUsingKB: number | null;
  percentAgentsUsingKB: number | null;
}

// Default data for file types
const fileTypes = [
  { name: 'PDF', value: 45, color: '#ef4444' },
  { name: 'Word', value: 30, color: '#3b82f6' },
  { name: 'Text', value: 15, color: '#10b981' },
  { name: 'Excel', value: 10, color: '#8b5cf6' }
];

/**
 * KnowledgeBaseAdoptionTab Component
 * 
 * Displays snapshot stats related to knowledge base usage
 */
export const KnowledgeBaseAdoptionTab: React.FC = () => {
  const { t } = useTranslation(namespace);
  // Get KB adoption data from Redux store using updated selectors and WebRootState type
  const kbAdoptionState = useSelector(
    (state: WebRootState) => selectKbAdoptionState(state)
  );
  const kbAdoptionData = useSelector(
    (state: WebRootState) => selectKbAdoptionData(state)
  );
  
  // Use loading state from kbAdoptionState
  const isLoading = kbAdoptionState.loading;

  // File types data isn't available from the API yet
  // We'll show a loading state for this section
  
  // Show loading state if data isn't ready
  if (isLoading || !kbAdoptionData) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">{t('common.loading')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Calculate average docs per KB using kbAdoptionData
  const averageDocsPerKB = kbAdoptionData.totalKBs && kbAdoptionData.totalKBs > 0 && kbAdoptionData.totalDocuments
    ? Math.round(kbAdoptionData.totalDocuments / kbAdoptionData.totalKBs)
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">{t('botUsageDashboard.kbAdoption.totalKnowledgeBases')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{kbAdoptionData.totalKBs ?? '0'}</div>
          <div className="mt-1 text-xs text-slate-500">
            {t('botUsageDashboard.kbAdoption.totalKnowledgeBasesDescription')}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">{t('botUsageDashboard.kbAdoption.totalDocuments')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{kbAdoptionData.totalDocuments ?? '0'}</div>
          {kbAdoptionData.totalDocuments && kbAdoptionData.totalKBs && (
            <div className="mt-1 text-xs text-slate-500">
              {t('botUsageDashboard.kbAdoption.averageDocsPerKB', { count: averageDocsPerKB })}
            </div>
          )}
          {(!kbAdoptionData.totalDocuments || !kbAdoptionData.totalKBs) && (
            <div className="mt-1 text-xs text-slate-500">
              {t('botUsageDashboard.kbAdoption.noDocuments')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">{t('botUsageDashboard.kbAdoption.agentsUsingKB')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{kbAdoptionData.agentsUsingKB ?? '0'}</div>
          {kbAdoptionData.agentsUsingKB && kbAdoptionData.percentAgentsUsingKB && (
            <>
              <div className="mt-2">
                <Progress value={kbAdoptionData.percentAgentsUsingKB} className="h-2" />
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {t('botUsageDashboard.kbAdoption.percentUsingKB', { percentage: kbAdoptionData.percentAgentsUsingKB })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">{t('botUsageDashboard.kbAdoption.fileTypes')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto mb-2" />
              <p className="text-xs text-slate-500">{t('botUsageDashboard.kbAdoption.dataNotAvailable')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 